import React, {useState} from 'react';
import {useRef} from 'react';

import {motion, AnimatePresence} from 'motion/react';
import {useIntl} from 'react-intl';

export const InteractiveHardware: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const intl = useIntl();
  const ref = useRef(null);
  // const isInView = useInView(ref, {once: true, amount: 0.3});
  const [activeFeature, setActiveFeature] = useState(0);

  const hardwareFeatures = [
    {
      title: intl.formatMessage({id: 'home.interactiveHardware.feature1Title'}),
      description: intl.formatMessage({
        id: 'home.interactiveHardware.feature1Description',
      }),
      image: '/images/stryd5/Still 5D - ON.png',
      detail: intl.formatMessage({
        id: 'home.interactiveHardware.feature1Detail',
      }),
    },
    {
      title: intl.formatMessage({id: 'home.interactiveHardware.feature2Title'}),
      description: intl.formatMessage({
        id: 'home.interactiveHardware.feature2Description',
      }),
      image: '/images/stryd5/Still 7A.png',
      detail: intl.formatMessage({
        id: 'home.interactiveHardware.feature2Detail',
      }),
    },
    {
      title: intl.formatMessage({id: 'home.interactiveHardware.feature3Title'}),
      description: intl.formatMessage({
        id: 'home.interactiveHardware.feature3Description',
      }),
      image: '/images/stryd5/Still 4A-Single.png',
      detail: intl.formatMessage({
        id: 'home.interactiveHardware.feature3Detail',
      }),
    },
    {
      title: intl.formatMessage({id: 'home.interactiveHardware.feature4Title'}),
      description: intl.formatMessage({
        id: 'home.interactiveHardware.feature4Description',
      }),
      image: '/images/stryd5/Still 5A.png',
      detail: intl.formatMessage({
        id: 'home.interactiveHardware.feature4Detail',
      }),
    },
    {
      title: intl.formatMessage({id: 'home.interactiveHardware.feature5Title'}),
      description: intl.formatMessage({
        id: 'home.interactiveHardware.feature5Description',
      }),
      image: '/images/stryd5/Still 5B - ON.png',
      detail: intl.formatMessage({
        id: 'home.interactiveHardware.feature5Detail',
      }),
    },
    {
      title: intl.formatMessage({id: 'home.interactiveHardware.feature6Title'}),
      description: intl.formatMessage({
        id: 'home.interactiveHardware.feature6Description',
      }),
      image: '/images/stryd5/STRYD_Array A.png',
      detail: intl.formatMessage({
        id: 'home.interactiveHardware.feature6Detail',
      }),
    },
  ];

  return (
    <section ref={ref} className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* NEW Badge */}
        <motion.div
          className="text-center mb-6"
          // initial={{opacity: 0, y: 20}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.6, ease: 'easeOut'}}
        >
          <span className="inline-block bg-orange-500 text-white text-sm font-bold px-4 py-2 rounded-full uppercase tracking-wide">
            {intl.formatMessage({id: 'home.interactiveHardware.badge'})}
          </span>
        </motion.div>

        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          // initial={{opacity: 0, y: 50}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, ease: 'easeOut'}}
        >
          <h2 className="text-4xl md:text-6xl font-light text-black mb-4">
            <strong className="font-bold">
              {intl.formatMessage({id: 'home.interactiveHardware.title'})}
            </strong>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            {intl.formatMessage({
              id: 'home.interactiveHardware.detailedDescription',
            })}
          </p>
        </motion.div>

        {/* Interactive Hardware Showcase */}
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left Side - Feature List */}
          <motion.div
            className="space-y-2 lg:flex-1 lg:basis-1/3"
            // initial={{opacity: 0, x: -50}}
            // animate={isInView ? {opacity: 1, x: 0} : {}}
            // transition={{duration: 0.8, delay: 0.3}}
          >
            {hardwareFeatures.map((feature, index) => (
              <motion.div
                key={index}
                className={`relative cursor-pointer transition-all duration-300 ${
                  activeFeature === index
                    ? 'bg-gray-100 rounded-2xl p-6 shadow-sm'
                    : 'bg-gray-50 rounded-2xl p-4 hover:bg-gray-100'
                }`}
                onClick={() => setActiveFeature(index)}
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Plus/Minus Icon */}
                    <motion.div
                      className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center"
                      animate={{
                        rotate: activeFeature === index ? 45 : 0,
                        backgroundColor:
                          activeFeature === index ? '#3B82F6' : '#E5E7EB',
                      }}
                      transition={{duration: 0.3}}
                    >
                      <motion.div
                        className="w-4 h-0.5 bg-white"
                        animate={{
                          rotate: activeFeature === index ? 90 : 0,
                          opacity: activeFeature === index ? 0 : 1,
                        }}
                        transition={{duration: 0.3}}
                      />
                      <motion.div
                        className="w-4 h-0.5 bg-white absolute"
                        animate={{
                          rotate: activeFeature === index ? 0 : 90,
                          opacity: activeFeature === index ? 1 : 1,
                        }}
                        transition={{duration: 0.3}}
                      />
                    </motion.div>

                    <div>
                      <h3 className="text-lg font-semibold text-black mb-1">
                        {feature.title}
                      </h3>
                      {/* <p className="text-sm text-gray-600">{feature.detail}</p> */}
                    </div>
                  </div>

                  {/* Arrow indicator */}
                  <motion.div
                    className="text-gray-400"
                    animate={{
                      rotate: activeFeature === index ? 90 : 0,
                      color: activeFeature === index ? '#3B82F6' : '#9CA3AF',
                    }}
                    transition={{duration: 0.3}}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </motion.div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence>
                  {activeFeature === index && (
                    <motion.div
                      initial={{opacity: 0, height: 0}}
                      animate={{opacity: 1, height: 'auto'}}
                      exit={{opacity: 0, height: 0}}
                      transition={{duration: 0.3, ease: 'easeInOut'}}
                      className="mt-4 pt-4 border-t border-gray-200"
                    >
                      <p className="text-gray-600 leading-relaxed text-sm">
                        {feature.description}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>

          {/* Right Side - Product Image */}
          <motion.div
            className="relative flex justify-center items-center lg:basis-2/3 flex-1"
            // initial={{opacity: 0, x: 50}}
            // animate={isInView ? {opacity: 1, x: 0} : {}}
            // transition={{duration: 0.8, delay: 0.5}}
          >
            <div className="flex justify-center items-center w-full h-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFeature}
                  initial={{opacity: 0, scale: 0.8, rotateY: 15}}
                  animate={{opacity: 1, scale: 1, rotateY: 0}}
                  exit={{opacity: 0, scale: 0.8, rotateY: -15}}
                  transition={{duration: 0.6, ease: 'easeInOut'}}
                  className="relative"
                >
                  <img
                    src={hardwareFeatures[activeFeature].image}
                    alt={hardwareFeatures[activeFeature].title}
                    className="w-full object-contain drop-shadow-2xl mx-auto z-10 relative rounded-xl"
                  />

                  {/* Glow effect */}
                  <motion.div
                    className="absolute inset-0 rounded-full blur-2xl z-0"
                    style={{
                      background:
                        'radial-gradient(circle, rgba(251, 146, 60, 0.4) 0%, rgba(251, 146, 60, 0.25) 40%, rgba(251, 146, 60, 0.1) 70%, transparent 100%)',
                    }}
                    animate={{
                      opacity: [0.5, 0.8, 0.5],
                      scale: [0.9, 1.3, 0.9],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Feature highlight overlay */}
              <motion.div
                className="absolute top-8 right-8 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-lg"
                initial={{opacity: 0, y: 20}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: 0.8}}
              >
                {/* <div className="text-sm font-medium text-gray-600 mb-1">
                  Current Feature
                </div> */}
                <div className="text-lg font-semibold text-black">
                  {hardwareFeatures[activeFeature].title}
                </div>
                <div className="text-sm text-blue-600 font-medium">
                  {hardwareFeatures[activeFeature].detail}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-16 text-center"
          // initial={{opacity: 0, y: 30}}
          // animate={isInView ? {opacity: 1, y: 0} : {}}
          // transition={{duration: 0.8, delay: 1.0}}
        >
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            {intl.formatMessage({
              id: 'home.interactiveHardware.bottomDescription',
            })}
          </p>

          {/* <motion.div
            className="flex justify-center gap-4"
            initial={{opacity: 0, scale: 0.8}}
            animate={isInView ? {opacity: 1, scale: 1} : {}}
            transition={{duration: 0.6, delay: 1.2}}
          >
            <div className="bg-gray-100 rounded-2xl px-6 py-3 flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">
                Precision engineered
              </span>
            </div>

            <div className="bg-gray-100 rounded-2xl px-6 py-3 flex items-center gap-3">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <span className="text-sm font-medium text-gray-700">
                Lightning fast
              </span>
            </div>
          </motion.div> */}
        </motion.div>
      </div>
    </section>
  );
};
